<template>
 <Teleport to="body">
 <div class="fixed inset-0 bg-black/50 flex items-center justify-center z-50" @click.self="close">
 <div data-tour="lesson-edit-modal" class="bg-surface dark:bg-surface-dark rounded-xl shadow-xl w-full max-w-sm mx-4">

 <!-- Header -->
 <div class="flex items-center justify-between px-5 pt-5 pb-3">
 <div>
 <h3 class="text-base font-semibold text-fg-base dark:text-fg-base-dark">
 {{ SCOPE_TITLES[scope] }}
 </h3>
 <p class="text-xs text-fg-muted dark:text-fg-muted-dark mt-0.5">{{ lesson.lesson_title }}</p>
 </div>
 <button @click="close" data-tour="lesson-edit-close-btn" class="p-1 text-fg-faint dark:text-fg-faint-dark rounded transition-colors">
 <XMarkIcon class="h-5 w-5" />
 </button>
 </div>

 <div class="px-5 pb-5">
 <!-- Locked: nothing on this lesson is changeable -->
 <template v-if="isPaid">
 <p class="text-sm text-fg-secondary dark:text-fg-secondary-dark">
 This lesson has been billed and can no longer be edited, cloned, or deleted.
 </p>
 </template>

 <template v-else>
 <!-- Action row -->
 <div data-tour="lesson-edit-action" class="flex rounded-lg border border-stroke dark:border-stroke-dark overflow-hidden" :class="action === 'clone' ? 'mb-5' : 'mb-2'">
 <button
 @click="action = 'edit'"
 :class="['flex-1 py-1.5 text-xs font-medium transition-colors',
 action === 'edit'
 ? 'bg-blue-600 text-white'
 : 'text-fg-secondary dark:text-fg-secondary-dark']"
 >Edit</button>
 <button
 @click="action = 'delete'"
 :class="['flex-1 py-1.5 text-xs font-medium transition-colors border-l border-stroke dark:border-stroke-dark',
 action === 'delete'
 ? 'bg-blue-600 text-white border-blue-600'
 : 'text-fg-secondary dark:text-fg-secondary-dark']"
 >Delete</button>
 <button
 @click="action = 'clone'"
 :class="['flex-1 py-1.5 text-xs font-medium transition-colors border-l border-stroke dark:border-stroke-dark',
 action === 'clone'
 ? 'bg-blue-600 text-white border-blue-600'
 : 'text-fg-secondary dark:text-fg-secondary-dark']"
 >Clone</button>
 </div>

 <!-- Scope toggle: which lesson(s) Edit/Delete applies to. Not meaningful for Clone. -->
 <div v-if="action !== 'clone'" data-tour="lesson-edit-scope" class="flex rounded-lg border border-stroke dark:border-stroke-dark overflow-hidden mb-5">
 <button
 v-if="singleAvailable"
 @click="toggle = 'lesson'"
 :class="['flex-1 py-1.5 text-xs font-medium transition-colors',
 toggle === 'lesson'
 ? 'bg-blue-600 text-white'
 : 'text-fg-secondary dark:text-fg-secondary-dark']"
 >This lesson</button>
 <button
 @click="toggle = 'series'"
 :class="['flex-1 py-1.5 text-xs font-medium transition-colors border-l border-stroke dark:border-stroke-dark',
 toggle === 'series'
 ? 'bg-blue-600 text-white border-blue-600'
 : 'text-fg-secondary dark:text-fg-secondary-dark',
 !singleAvailable && 'border-l-0']"
 >Lesson series</button>
 </div>

 <!-- Single-lesson edit fields -->
 <template v-if="scope === 'single'">
 <div class="space-y-3">
 <div>
 <label class="block text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">Date</label>
 <input type="date" v-model="form.date" :class="inputClass" />
 </div>
 <div>
 <label class="block text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">Time</label>
 <input type="time" v-model="form.time" :class="inputClass" />
 </div>
 <div>
 <label class="block text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">Duration</label>
 <select v-model.number="form.duration" :class="inputClass">
 <option v-for="d in DURATIONS" :key="d" :value="d">{{ d }} minutes</option>
 </select>
 <p v-if="priceChangeNote && !priceTouched" class="mt-1 text-xs text-fg-muted dark:text-fg-muted-dark">{{ priceChangeNote }}</p>
 </div>
 <div v-if="isAdmin">
 <label class="block text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">Price per lesson ($)</label>
 <input type="number" min="0" step="0.01" v-model="form.price" @input="priceTouched = true" :class="inputClass" />
 </div>
 <div>
 <label class="block text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">Location</label>
 <select v-model="form.location" :class="inputClass">
 <option value="">— none —</option>
 <option v-for="loc in LOCATION_OPTIONS" :key="loc" :value="loc">{{ loc }}</option>
 </select>
 </div>
 <!-- Admin-only: waive this one lesson's charge. Teacher pay is unaffected. -->
 <label v-if="isAdmin" class="flex items-start gap-2 pt-1 text-sm text-fg-base dark:text-fg-base-dark">
 <input v-model="form.isComp" type="checkbox" class="mt-0.5 rounded border-stroke-strong dark:border-stroke-strong-dark" />
 <span>
 Complimentary (no charge)
 <span class="block text-xs text-fg-muted dark:text-fg-muted-dark">This lesson is never billed. The teacher is still paid.</span>
 </span>
 </label>
 </div>
 </template>

 <!-- Series edit fields -->
 <template v-else-if="scope === 'series'">
 <div class="space-y-3">
 <div>
 <label class="block text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">Starting</label>
 <input type="date" v-model="form.effectiveDate" :min="todayIso" :class="inputClass" />
 <p class="mt-1 text-xs text-fg-muted dark:text-fg-muted-dark">Lessons before this date are unaffected.</p>
 </div>
 <div>
 <label class="block text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">Time</label>
 <input type="time" v-model="form.time" :class="inputClass" />
 </div>
 <div>
 <label class="block text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">
 Day of week
 <span class="text-fg-faint dark:text-fg-faint-dark font-normal ml-1">(optional)</span>
 </label>
 <select v-model="form.weekday" :class="inputClass">
 <option value="">No change</option>
 <option value="0">Sunday</option>
 <option value="1">Monday</option>
 <option value="2">Tuesday</option>
 <option value="3">Wednesday</option>
 <option value="4">Thursday</option>
 <option value="5">Friday</option>
 <option value="6">Saturday</option>
 </select>
 </div>
 <div>
 <label class="block text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">Duration</label>
 <select v-model.number="form.duration" :class="inputClass">
 <option v-for="d in DURATIONS" :key="d" :value="d">{{ d }} minutes</option>
 </select>
 <p v-if="priceChangeNote && !priceTouched" class="mt-1 text-xs text-fg-muted dark:text-fg-muted-dark">{{ priceChangeNote }} Applies starting the date above — if this series has billing history, this will start a new series so the old terms stay on record.</p>
 </div>
 <div v-if="isAdmin">
 <label class="block text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">Price per lesson ($)</label>
 <input type="number" min="0" step="0.01" v-model="form.price" @input="priceTouched = true" :class="inputClass" />
 </div>
 <div>
 <label class="block text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">Location</label>
 <select v-model="form.location" :class="inputClass">
 <option value="">— none —</option>
 <option v-for="loc in LOCATION_OPTIONS" :key="loc" :value="loc">{{ loc }}</option>
 </select>
 </div>
 </div>
 </template>

 <!-- Delete lesson series (end series) fields -->
 <template v-else-if="scope === 'last-lesson'">
 <!-- Every end needs a reason (plan 61); "Rescheduling" opens New Lesson instead of ending anything here. -->
 <StopReasonFields v-if="!isMembership" v-model="form.stop" allow-reschedule class="mb-3" />
 <template v-if="isReschedule">
 <p class="text-xs text-fg-muted dark:text-fg-muted-dark">
 Set up the new schedule next. These lessons end the day before it starts, and nothing changes until you save it.
 </p>
 </template>
 <template v-else>
 <div class="space-y-3">
 <div>
 <label class="block text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">End series after</label>
 <input type="date" v-model="form.lastLessonDate" :min="todayIso" :class="inputClass" />
 </div>
 </div>
 <p class="mt-3 text-xs text-fg-muted dark:text-fg-muted-dark">
 The series will run through its last scheduled lesson on or before this date. All later lessons will be cancelled.
 </p>
 </template>
 </template>

 <!-- Delete single lesson -->
 <template v-else-if="scope === 'cancel'">
 <p class="text-sm text-fg-secondary dark:text-fg-secondary-dark">
 This will permanently remove this lesson from the calendar.  This cannot be undone.  Consider marking this lesson excused instead.  
 </p>
 </template>

 <!-- Clone fields -->
 <template v-else>
 <div class="space-y-3">
 <div>
 <label class="block text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">New date</label>
 <input type="date" v-model="form.cloneDate" :class="inputClass" />
 </div>
 <div>
 <label class="block text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">
 Time
 <span class="text-fg-faint dark:text-fg-faint-dark font-normal ml-1">(optional — defaults to same time)</span>
 </label>
 <input type="time" v-model="form.time" :class="inputClass" />
 </div>
 </div>
 <p class="mt-3 text-xs text-fg-muted dark:text-fg-muted-dark">
 Creates a one-off lesson with the same student, teacher, and instrument. The new lesson will appear after the next sync.
 </p>
 </template>
 </template>

 <!-- Error -->
 <p v-if="error" class="mt-3 text-sm text-red-600 dark:text-red-400">{{ error }}</p>

 <!-- Actions -->
 <div class="flex justify-end gap-2 mt-5">
 <button @click="close"
 class="px-4 py-2 text-sm font-medium rounded-lg border border-stroke dark:border-stroke-dark text-fg-secondary dark:text-fg-secondary-dark transition-colors">
 {{ isPaid ? 'Close' : 'Cancel' }}
 </button>
 <button v-if="!isPaid" @click="submit"
 :disabled="isSubmitting"
 :class="['px-4 py-2 text-sm font-medium rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors',
 isDelete && !isReschedule ? 'bg-red-600' : 'bg-blue-600']">
 {{ isSubmitting
 ? (scope === 'clone' ? 'Cloning…' : scope === 'cancel' ? 'Deleting…' : 'Saving…')
 : (scope === 'clone' ? 'Clone' : isReschedule ? 'Continue' : scope === 'last-lesson' ? 'End series' : scope === 'cancel' ? 'Delete' : 'Save') }}
 </button>
 </div>
 </div>
 </div>
 </div>
 </Teleport>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { useApi } from '../../composables/useApi.js'
import { lessonLockTier, notifyLessonsChanged } from '../../composables/useCalendarData.js'
import { LESSON_LOCATION_OPTIONS } from '../../utils/lessonSeriesConstants.js'
import { emptyStop, openReschedule, stopFields } from '../../utils/stopReasons.js'
import StopReasonFields from './StopReasonFields.vue'
import { useDemoMode } from '../../composables/useDemoMode'

const props = defineProps({
 lesson: { type: Object, required: true },
 allowLockedSingleEdit: { type: Boolean, default: false },
})

const emit = defineEmits(['close', 'saved'])

const { editLesson, cloneLesson, cancelLesson } = useApi()

const effectivePermissions = useDemoMode().effectivePermissionsFor(() => {
 const user = JSON.parse(localStorage.getItem('user') || 'null')
 return user?.permissions
})
const isAdmin = computed(() => effectivePermissions.value.includes('admin'))

const inputClass = 'w-full px-3 py-2 text-sm rounded-lg border border-stroke dark:border-stroke-dark bg-surface dark:bg-surface-dark text-fg-base dark:text-fg-base-dark focus:outline-none focus:ring-2 focus:ring-blue-500'

const DURATIONS = [30, 45, 60, 90]
const LOCATION_OPTIONS = LESSON_LOCATION_OPTIONS

const SCOPE_TITLES = {
 single: 'Edit Lesson',
 series: 'Edit Lesson',
 'last-lesson': 'End Series',
 cancel: 'Delete Lesson',
 clone: 'Clone Lesson',
}

// ── Helpers ─────────────────────────────────────────────────────────────────

const amPmToHHMM = (ampm) => {
 if (!ampm) return ''
 const m = ampm.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
 if (!m) return ''
 let h = parseInt(m[1])
 const min = m[2]
 const period = m[3].toUpperCase()
 if (period === 'PM' && h !== 12) h += 12
 if (period === 'AM' && h === 12) h = 0
 return `${String(h).padStart(2, '0')}:${min}`
}

const hhmmToAmPm = (hhmm) => {
 if (!hhmm) return null
 const [h, min] = hhmm.split(':').map(Number)
 const period = h >= 12 ? 'PM' : 'AM'
 const hour12 = h % 12 || 12
 return `${hour12}:${String(min).padStart(2, '0')} ${period}`
}

// Default clone date: +7 days from lesson date (same time next week)
const defaultCloneDate = () => {
 if (!props.lesson.lesson_date) return ''
 const d = new Date(props.lesson.lesson_date + 'T00:00:00')
 d.setDate(d.getDate() + 7)
 return d.toISOString().slice(0, 10)
}

// ── Lock tier ────────────────────────────────────────────────────────────────
// Mirrors the backend's lesson_lock_tier() — the single source of truth shared
// by every edit/delete/clone/attendance guard. 'paid' blocks everything below,
// for everyone. 'locked' (dispute window closed, nothing charged yet) blocks
// only the This-Lesson scope for non-admins — callers pass
// allowLockedSingleEdit=isAdmin. Lesson-series scope (Edit or Delete) was never
// gated by the dispute window, only by 'paid', since it acts on future,
// not-yet-happened occurrences rather than this specific lesson's own record.

const tier = computed(() => lessonLockTier(props.lesson))
const isPaid = computed(() => tier.value === 'paid')
const singleAvailable = computed(() => tier.value === 'open' || props.allowLockedSingleEdit)

const todayIso = new Date().toISOString().slice(0, 10)

// ── Form state ───────────────────────────────────────────────────────────────

const action = ref('edit')   // 'edit' | 'delete' | 'clone'
const toggle = ref(singleAvailable.value ? 'lesson' : 'series')  // 'lesson' | 'series' — ignored for clone

const scope = computed(() => {
 if (action.value === 'clone') return 'clone'
 if (action.value === 'delete') return toggle.value === 'series' ? 'last-lesson' : 'cancel'
 return toggle.value === 'series' ? 'series' : 'single'
})
const isDelete = computed(() => scope.value === 'cancel' || scope.value === 'last-lesson')

const isSubmitting = ref(false)
const error = ref(null)

const form = ref({
 time: amPmToHHMM(props.lesson.lesson_time),
 date: props.lesson.lesson_date || '',
 weekday: '',
 effectiveDate: props.lesson.lesson_date || todayIso,
 lastLessonDate: '',
 cloneDate: defaultCloneDate(),
 duration: props.lesson.duration_minutes || 30,
 location: props.lesson.location || '',
 isComp: props.lesson.billing_route === 'comp',
 price: props.lesson.price_cents == null ? '' : (props.lesson.price_cents / 100).toFixed(2),
 stop: emptyStop(),
})

// Membership lessons end with the membership, so they take no stop reason.
const isMembership = props.lesson.billing_route === 'membership'
const isReschedule = computed(() => scope.value === 'last-lesson' && form.value.stop.category === 'reschedule')

// ── Price preview ───────────────────────────────────────────────────────────
// Mirrors the backend's re-pricing in lesson_edits.py: same hourly rate, new length.

const currentDuration = props.lesson.duration_minutes || 30

const newPriceCentsForDuration = (minutes) => {
 if (props.lesson.price_cents == null) return null
 const hourlyRateCents = (props.lesson.price_cents / currentDuration) * 60
 return Math.round((hourlyRateCents * minutes) / 60)
}

const priceChangeNote = computed(() => {
 if (form.value.duration === currentDuration) return ''
 const newCents = newPriceCentsForDuration(form.value.duration)
 if (newCents == null) return ''
 return `New price: $${(newCents / 100).toFixed(2)} (was $${(props.lesson.price_cents / 100).toFixed(2)}).`
})

// Admin price field follows the duration re-price until the admin types their own value.
const priceTouched = ref(false)
watch(() => form.value.duration, (minutes) => {
 if (priceTouched.value) return
 const cents = newPriceCentsForDuration(minutes)
 if (cents != null) form.value.price = (cents / 100).toFixed(2)
})

// ── Submit ───────────────────────────────────────────────────────────────────

const submit = async () => {
 error.value = null
 isSubmitting.value = true

 try {
 if (scope.value === 'clone') {
 if (!form.value.cloneDate) {
 error.value = 'Date is required.'
 return
 }
 const body = { date: form.value.cloneDate }
 const newAmPm = hhmmToAmPm(form.value.time)
 if (newAmPm && newAmPm !== props.lesson.lesson_time) {
 body.start_time = newAmPm
 }
 await cloneLesson(props.lesson.id, body)
 notifyLessonsChanged()
 emit('saved', { cloned: true })
 close()
 return
 }

 if (scope.value === 'cancel') {
 await cancelLesson(props.lesson.id)
 notifyLessonsChanged()
 emit('saved', { cancelled_lesson_ids: [props.lesson.id] })
 close()
 return
 }

 if (scope.value === 'last-lesson') {
 if (isReschedule.value) {
 openReschedule({ replacesLessonId: props.lesson.id })
 close()
 return
 }
 if (!isMembership && !form.value.stop.category) {
 error.value = 'Pick why the lessons are ending.'
 return
 }
 if (!form.value.lastLessonDate) {
 error.value = 'Date of final lesson is required.'
 return
 }
 if (form.value.lastLessonDate < todayIso) {
 error.value = "End date can't be in the past."
 return
 }
 const response = await editLesson(props.lesson.id, {
 lesson_only: false,
 end_date: form.value.lastLessonDate,
 ...(isMembership ? {} : stopFields(form.value.stop)),
 })
 notifyLessonsChanged()
 emit('saved', { ...(response?.lesson ?? {}), cancelled_lesson_ids: response?.cancelled_lesson_ids ?? [] })
 close()
 return
 }

 const lessonOnly = scope.value === 'single'
 const body = { lesson_only: lessonOnly }

 const newAmPm = hhmmToAmPm(form.value.time)
 if (newAmPm && newAmPm !== props.lesson.lesson_time) {
 body.start_time = newAmPm
 }

 if (lessonOnly) {
 if (form.value.date && form.value.date !== props.lesson.lesson_date) {
 body.date = form.value.date
 }
 } else {
 if (form.value.weekday !== '') {
 body.weekday = parseInt(form.value.weekday)
 }
 if (!form.value.effectiveDate) {
 error.value = 'Start date is required.'
 return
 }
 if (form.value.effectiveDate < todayIso) {
 error.value = 'Start date must be today or later.'
 return
 }
 body.effective_date = form.value.effectiveDate
 }

 if (form.value.duration !== currentDuration) {
 body.duration_minutes = form.value.duration
 }

 if (form.value.location !== (props.lesson.location || '')) {
 body.location = form.value.location
 }

 if (isAdmin.value && priceTouched.value && form.value.price !== '') {
 const cents = Math.round(parseFloat(form.value.price) * 100)
 if (Number.isNaN(cents) || cents < 0) {
 error.value = 'Enter a valid price.'
 return
 }
 if (cents !== props.lesson.price_cents) body.price_cents = cents
 }

 if (lessonOnly && isAdmin.value && form.value.isComp !== (props.lesson.billing_route === 'comp')) {
 body.is_comp = form.value.isComp
 }

 const ignoredKeys = lessonOnly ? ['lesson_only'] : ['lesson_only', 'effective_date']
 if (Object.keys(body).filter((k) => !ignoredKeys.includes(k)).length === 0) {
 error.value = 'No changes to save.'
 return
 }

 const response = await editLesson(props.lesson.id, body)
 notifyLessonsChanged()
 emit('saved', { ...(response?.lesson ?? {}), cancelled_lesson_ids: response?.cancelled_lesson_ids ?? [] })
 close()
 } catch (e) {
 error.value = e.message || 'Failed to save changes.'
 } finally {
 isSubmitting.value = false
 }
}

const close = () => emit('close')
</script>
